import { Order } from "./types";

/**
 * Comparator for descending sort order
 */
export function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  // Handle null/undefined values
  if (bValue == null && aValue == null) return 0;
  if (bValue == null) return -1;
  if (aValue == null) return 1;

  // String comparison (case-insensitive)
  if (typeof aValue === "string" && typeof bValue === "string") {
    return bValue.toLowerCase().localeCompare(aValue.toLowerCase());
  }

  // Numeric/default comparison
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

/**
 * Returns a comparator function based on sort order
 */
export function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string | any },
  b: { [key in Key]: number | string | any }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

/**
 * Stable sort that preserves original order for equal elements
 * This ensures consistent sorting behavior across browsers
 */
export function stableSort<T>(
  array: T[],
  comparator: (a: T, b: T) => number
): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

/**
 * Filter data based on search term across specified fields
 */
export function filterBySearch<T>(
  data: T[],
  searchTerm: string,
  searchFields?: string[]
): T[] {
  if (!searchTerm || searchTerm.trim() === "") {
    return data;
  }

  const lowerSearchTerm = searchTerm.toLowerCase();

  return data.filter((item) => {
    // If specific search fields are provided, only search those
    if (searchFields && searchFields.length > 0) {
      return searchFields.some((field) => {
        const value = (item as any)[field];
        if (typeof value === "string") {
          return value.toLowerCase().includes(lowerSearchTerm);
        }
        if (typeof value === "number") {
          return value.toString().includes(lowerSearchTerm);
        }
        return false;
      });
    }

    // Otherwise, search all string fields
    return Object.values(item as any).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(lowerSearchTerm);
      }
      if (typeof value === "number") {
        return value.toString().includes(lowerSearchTerm);
      }
      return false;
    });
  });
}
